import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { useSearchParams } from "react-router-dom";
import { skipToken } from "@reduxjs/toolkit/query";
import { LockClosedIcon } from "@heroicons/react/20/solid";

import { getHtml } from "../../../app/rteUtils";
import { getParamAsInt, isNullOrWhiteSpace } from "../../../app/utils";
import { SelectDevelopmentView } from "../../../components/input/SelectDevelopmentView";
import RtkqResultViewer from "../../../components/RtkqResultViewer";
import ButtonWithConfirm from "../../../components/ButtonWithConfirm";
import { useFinalizeDraftMutation, usePreviewDraftQuery } from "./draftingApiSlice";

export default function Preview() {
	const [queryParams] = useSearchParams();
	const draftId = getParamAsInt(queryParams, "draftId");
	if (draftId === undefined) return null;

	const [view, setView] = useState<string>("legacy");
	const [responseHtml, setResponseHtml] = useState<string | undefined>();
	const [sourcesHtml, setSourcesHtml] = useState<string | undefined>();

	const navigate = useNavigate();

	const result = usePreviewDraftQuery(draftId ?? skipToken);
	const data = result.data;

	const [finalizeDraft] = useFinalizeDraftMutation();

	useEffect(() => {
		if (!data) return;

		const response = getHtml(data.responseFormat, data.responseContent, data.responseMarkup);
		setResponseHtml(response);

		if (!isNullOrWhiteSpace(data.sourcesContent)) {
			const sources = getHtml(data.sourcesFormat, data.sourcesContent, data.sourcesMarkup);
			setSourcesHtml(sources);
		} else {
			setSourcesHtml(undefined);
		}
	}, [data]);

	const handleFinalize = () => {
		if (!draftId) return;

		finalizeDraft(draftId)
			.unwrap()
			.then(() => {
				navigate(`/scoreReport?draftId=${draftId}`);
			})
			.catch((error) => {
				console.log(error);
			});
	};

	const handlePrint = (event: React.FormEvent<HTMLButtonElement>) => {
		window.print();
	};

	return (
		<section>
			<div className="flex justify-between mb-4 print:hidden">
				<div className="w-3/4">
					<h1>Draft Preview</h1>
				</div>
				<SelectDevelopmentView
					value={view}
					setValue={(v) => setView(v as string)}
					simpleListBoxId="viewFilter"
					options={[
						{ value: "legacy", label: "Legacy" },
						{ value: "raw", label: "Raw Data" },
					]}
					fieldClasses="w-64"
				/>
			</div>
			{view === "legacy" && (
				<>
					{result.isFetching && <p>Fetching</p>}
					{result.isSuccess && data && (
						<>
							{<h2>{data.title}</h2>}
							{responseHtml && responseHtml.length > 0 && (
								<div
									className="ProseMirror mt-2"
									dangerouslySetInnerHTML={{
										__html: responseHtml,
									}}></div>
							)}
							{sourcesHtml && sourcesHtml.length > 0 && (
								<>
									<h3 className="mt-8">Cited Sources</h3>
									<div
										className="ProseMirror mt-2"
										dangerouslySetInnerHTML={{
											__html: sourcesHtml,
										}}></div>
								</>
							)}
							<div className="flex justify-between mt-8 print:hidden">
								<div>
									<button className="btn btn-secondary" onClick={handlePrint}>
										Print
									</button>
								</div>
								<div>
									{data.canFinalize && (
										<>
											<ButtonWithConfirm
												className="btn btn-alert"
												title="Wait!"
												message="You are about to finalize an essay. You will not be able to return and make further changes. Are you sure you want to finalize this essay?"
												confirmLabel="Yes"
												cancelLabel="Not Yet"
												onConfirm={handleFinalize}>
												<LockClosedIcon className="h-4 inline mr-2" />
												Finalize Essay
											</ButtonWithConfirm>
										</>
									)}
								</div>
							</div>
						</>
					)}
				</>
			)}
			{view === "raw" && <RtkqResultViewer heading="Preview Data" result={result} />}
		</section>
	);
}
